import { useEffect, useState } from 'react';
import { Box, Container, Dialog, DialogContent, DialogActions, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@qwickapps/auth-client';
import { useMiddlewareClient } from '@qwickforge/react-client';
import { Button, Text } from '@qwickapps/react-framework';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { user, session, signOut } = useAuth();
  const client = useMiddlewareClient();
  const [loading, setLoading] = useState(true);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    checkUserStatus();
  }, [user, session, client]);

  const checkUserStatus = async () => {
    try {
      // Wait for auth to initialize
      if (!user || !session?.accessToken || !client) {
        return;
      }

      setLoading(true);
      setError('');

      // Get user profile for display name
      const profile = await client.profile.get();

      // Extract display name
      const displayName = profile.fullName || user.email?.split('@')[0] || 'User';
      setUserName(displayName);

      // Check if user has any devices - this determines if they're a returning user
      const data = await client.devices.list();
      const devices = Array.isArray(data) ? data : data.devices || [];

      if (devices.length > 0) {
        // Returning user with devices - show welcome back dialog
        setIsReturningUser(true);
        setShowWelcomeBack(true);
      } else {
        // New user or user without devices - redirect to device setup
        setIsReturningUser(false);
        navigate('/setup/device');
      }
    } catch (error: any) {
      console.error('Failed to check user status:', error);
      setError('Failed to load your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    setShowWelcomeBack(false);
    navigate('/dashboard');
  };

  const handleSignOutAndSwitch = async () => {
    setShowWelcomeBack(false);
    await signOut();
    navigate('/login');
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Text variant="body1" color="var(--theme-secondary)" content="Loading your account..." />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Dialog
        open={showWelcomeBack}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 3 }}>
          <Box sx={{ mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'var(--theme-primary)' }} />
          </Box>
          <Text variant="h5" sx={{ mb: 1, fontWeight: 600 }} content={`Welcome back, ${userName}!`} />
          <Text
            variant="body1"
            color="var(--theme-primary-dark)"
            content="You're already signed in and ready to go."
          />
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleContinue}
          >
            Continue to Dashboard
          </Button>
          <Button
            fullWidth
            variant="text"
            size="medium"
            onClick={handleSignOutAndSwitch}
            sx={{ color: 'var(--theme-primary-dark)' }}
          >
            Not {userName}? Sign in with a different account
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
